import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { getAccountGroupIdForAccount } from '../../Modules/assets/asset-movement/asset-movement.utils';

export interface DashboardFilters {
  account_id?: number[] | null;
  status?: number[] | null;
  category?: number[] | null;
  priority_id?: number[] | null;
  assigned_to?: number[] | null;
}

export interface DashboardData {
  cardCounts: CardCounts;
  requests: any[];
  requestTypeData: ChartDatum[];
  assigneeData: ChartDatum[];
  priorityData: ChartDatum[];
  statusData: ChartDatum[];
  projectPhaseData: ChartDatum[];
  requestAgeData: ChartDatum[];
}

export interface CardCounts {
  totalRequests: number;
  openRequests: number;
  closedRequests: number;
  pendingRequests: number;
}

export interface ChartDatum {
  name: string;
  value: number;
  extra?: {
    displayName: string;
    count: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AdminDashboardService {
  private readonly baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch all dashboard data in parallel for optimal performance
   */
  fetchDashboardData(filters: DashboardFilters): Observable<DashboardData> {
    return forkJoin({
      cardCounts: this.fetchCardCounts(filters),
      requests: this.fetchRequests(filters),
      requestTypeData: this.fetchRequestTypeData(filters),
      assigneeData: this.fetchAssigneeData(filters),
      priorityData: this.fetchPriorityData(filters),
      statusData: this.fetchStatusData(filters),
      projectPhaseData: this.fetchProjectPhaseData(filters),
      requestAgeData: this.fetchRequestAgeData(filters)
    });
  }

  /**
   * Fetch card counts
   */
  fetchCardCounts(filters: DashboardFilters): Observable<CardCounts> {
    const payload = { account_id: filters.account_id || null };
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_count`, payload).pipe(
      map(res => {
        if (res.success && res.data?.[0]) {
          const counts = res.data[0];
          return {
            totalRequests: counts.total_issues || 0,
            openRequests: counts.open_issues || 0,
            closedRequests: counts.closed_issues || 0,
            pendingRequests: counts.pending_issues || 0
          };
        }
        return {
          totalRequests: 0,
          openRequests: 0,
          closedRequests: 0,
          pendingRequests: 0
        };
      })
    );
  }

  /**
   * Fetch filtered requests
   */
  fetchRequests(filters: DashboardFilters): Observable<any[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_filtered_issues`, filters).pipe(
      map(res => res.success ? res.data || [] : [])
    );
  }

  /**
   * Fetch request type data (by category)
   */
  fetchRequestTypeData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_by_categories`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          const aggregated: { [key: string]: number } = {};
          res.data.forEach((item: any) => {
            const key = item.issue_category;
            const count = Number(item.total_requests ?? item.issues_count ?? 0);
            aggregated[key] = (aggregated[key] || 0) + count;
          });
          return Object.keys(aggregated).map(key => ({
            name: `(${aggregated[key]}) ${key}`,
            value: aggregated[key],
            extra: {
              displayName: key,
              count: aggregated[key]
            }
          }));
        }
        return [];
      })
    );
  }

  /**
   * Fetch full issues by categories with subcategories
   */
  fetchIssuesByCategories(filters: DashboardFilters): Observable<any[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_by_categories`, filters).pipe(
      map(res => {
        if (res.success && Array.isArray(res.data)) {
          return res.data;
        }
        if (Array.isArray(res)) {
          return res;
        }
        return [];
      })
    );
  }

  /**
   * Fetch assignee data
   */
  fetchAssigneeData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_by_assignee`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          return res.data.map((a: any) => {
            const displayName = a.role_name || `User ${a.assigned_to}`;
            return {
              name: `${displayName} (${a.issue_count})`,
              value: a.issue_count,
              extra: {
                displayName,
                count: a.issue_count
              }
            };
          });
        }
        return [];
      })
    );
  }

  /**
   * Fetch priority data
   */
  fetchPriorityData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_by_priority`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          return res.data.map((item: any) => ({
            name: `${item.priority} (${item.issue_count})`,
            value: item.issue_count,
            extra: {
              displayName: item.priority,
              count: item.issue_count
            }
          }));
        }
        return [];
      })
    );
  }

  /**
   * Fetch status data
   */
  fetchStatusData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_by_status`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          return res.data.map((item: any) => ({
            name: `${item.status_name} (${item.issue_count})`,
            value: item.issue_count,
            extra: {
              displayName: item.status_name,
              count: item.issue_count
            }
          }));
        }
        return [];
      })
    );
  }

  /**
   * Fetch project phase data
   */
  fetchProjectPhaseData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_project_phases`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          return res.data.map((item: any) => ({
            name: `${item.project_name || item.account_name} (${item.count})`,
            value: item.count,
            extra: {
              displayName: item.project_name || item.account_name,
              count: item.count
            }
          }));
        }
        return [];
      })
    );
  }

  /**
   * Fetch request age data
   */
  fetchRequestAgeData(filters: DashboardFilters): Observable<ChartDatum[]> {
    return this.http.post<any>(`${this.baseUrl}/fetch_issues_age`, filters).pipe(
      map(res => {
        if (res.success && res.data?.length) {
          return res.data.map((item: any) => ({
            name: `${item.age_in_days} Days (${item.issue_count})`,
            value: item.issue_count,
            extra: {
              displayName: `${item.age_in_days} Days`,
              count: item.issue_count
            }
          }));
        }
        return [];
      })
    );
  }

  /**
   * Fetch dropdown data
   */
  fetchDropdowns(accountId: string | null): Observable<{
    status: StatusItem[];
    priority: PriorityItem[];
    category: CategoryItem[];
    assignee: AssigneeItem[];
  }> {
    const accountGroupId = getAccountGroupIdForAccount(accountId);
    const category$ = accountGroupId != null
      ? this.http.post<CategoryItem[]>(`${this.baseUrl}/category_dropdown`, { account_group_id: accountGroupId })
      : of<CategoryItem[]>([]);

    return forkJoin({
      status: this.http.get<StatusItem[]>(`${this.baseUrl}/status_dropdown`),
      priority: this.http.get<PriorityItem[]>(`${this.baseUrl}/priority_dropdown`),
      category: category$,
      assignee: this.http.post<AssigneeItem[]>(`${this.baseUrl}/assignee_dropdown`, { account_id: accountId })
    });
  }
}

export interface StatusItem {
  status_id: number;
  status: string;
}

export interface ProjectItem {
  account_id: number;
  project_name: string;
}

export interface PriorityItem {
  priority_id: number;
  priority: string;
}

export interface CategoryItem {
  issue_category_id: number;
  issue_category: string;
}

export interface AssigneeItem {
  user_id: number;
  first_name: string;
  last_name: string;
}


